import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Sprout, Plus, Minus, Package } from 'lucide-react'
import { api } from '../lib/api'

const C = {
  bg: 'var(--color-bg)', bgCard: 'var(--color-bg-card)', border: 'var(--color-border)',
  amber: 'var(--color-primary)', green: '#2D6A2F', text: 'var(--color-text)',
  muted: 'var(--color-text-muted)', faint: 'var(--color-text-faint)', danger: '#F87171',
}

// Field names mirror the backend (internal/seeds): common_name, quantity_g, etc.
interface SeedInventory {
  id: string
  common_name: string
  species: string
  supplier: string
  lot_number: string
  quantity_g: number | null
  quantity_seeds: number | null
  germination_rate_pct: number | null
  purchase_date: string | null
  expiry_date: string | null
  storage_location: string
  notes: string
  created_at: string
}

interface SeedUsage {
  id: string
  seed_inventory_id: string
  used_at: string
  quantity_g: number | null
  quantity_seeds: number | null
  purpose: string
  field_id: string | null
  notes: string
}

export function SeedsPage() {
  const qc = useQueryClient()
  const [showAddSeed, setShowAddSeed] = useState(false)
  const [showUseForm, setShowUseForm] = useState<string | null>(null)
  const [form, setForm] = useState({
    common_name: '', species: '', supplier: '', lot_number: '',
    quantity_g: '', germination_rate_pct: '',
    purchase_date: '', expiry_date: '', storage_location: '', notes: '',
  })
  const [useForm, setUseForm] = useState({ quantity_g: '', purpose: 'propagation', notes: '' })

  // Backend wraps the list as { seeds: [...] }; guard against any shape so a bad
  // response can never crash the page (was: `u.reduce is not a function`).
  const { data: seeds = [], isLoading } = useQuery<SeedInventory[]>({
    queryKey: ['seeds'],
    queryFn: () => api.get('/seeds').then(r => (Array.isArray(r.data) ? r.data : r.data?.seeds ?? [])),
  })

  useQuery<SeedUsage[]>({
    queryKey: ['seeds-usage'],
    queryFn: () => api.get('/seeds/usage').then(r => (Array.isArray(r.data) ? r.data : r.data?.usage ?? [])),
  })

  // Empty date string → null; otherwise RFC3339 (the Go *time.Time decoder rejects "YYYY-MM-DD").
  const isoOrNull = (d: string) => (d ? new Date(d + 'T00:00:00').toISOString() : null)

  const createSeed = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/seeds', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['seeds'] }); setShowAddSeed(false); setForm({ common_name: '', species: '', supplier: '', lot_number: '', quantity_g: '', germination_rate_pct: '', purchase_date: '', expiry_date: '', storage_location: '', notes: '' }) },
  })

  const logUsage = useMutation({
    mutationFn: ({ seedId, body }: { seedId: string; body: Record<string, unknown> }) =>
      api.post(`/seeds/${seedId}/usage`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['seeds'] }); qc.invalidateQueries({ queryKey: ['seeds-usage'] }); setShowUseForm(null) },
  })

  const label: React.CSSProperties = { fontSize: 11, color: C.muted, marginBottom: 4, display: 'block' }
  const input: React.CSSProperties = { width: '100%', background: 'var(--color-bg-hover)', border: `1px solid ${C.border}`, borderRadius: 6, padding: '7px 10px', color: C.text, fontSize: 13 }
  const btn = (variant: 'primary' | 'ghost' | 'danger'): React.CSSProperties => ({
    padding: '7px 12px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 500,
    background: variant === 'primary' ? C.amber : variant === 'danger' ? 'rgba(248,113,113,0.15)' : 'rgba(255,255,255,0.06)',
    color: variant === 'primary' ? 'var(--color-bg)' : variant === 'danger' ? C.danger : C.muted,
  })

  const totalKg = seeds.reduce((s, i) => s + (i.quantity_g ?? 0), 0) / 1000
  const speciesCount = new Set(seeds.map(s => s.species).filter(Boolean)).size

  return (
    <main id="main-content" style={{ padding: '28px 32px', maxWidth: 1000, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>Seed Inventory</h1>
          <p style={{ fontSize: 13, color: C.muted, margin: '4px 0 0' }}>
            Track seed stock, germination rates, suppliers, and usage
          </p>
        </div>
        <button style={{ ...btn('primary'), padding: '8px 14px', fontSize: 13 }} onClick={() => setShowAddSeed(true)}>
          <Plus size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
          Add Seeds
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Seed lots', value: seeds.length, icon: Package },
          { label: 'Species', value: speciesCount, icon: Sprout },
          { label: 'Total stock (kg)', value: totalKg.toFixed(2), icon: Package },
        ].map(({ label: l, value, icon: Icon }) => (
          <div key={l} style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <Icon size={14} color={C.amber} />
              <span style={{ fontSize: 12, color: C.muted }}>{l}</span>
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Add seed form */}
      {showAddSeed && (
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 24px', marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: C.text }}>Add Seed Lot</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={label}>Variety / Cultivar Name</label>
              <input style={input} placeholder="e.g. Tommy Atkins, Keitt" value={form.common_name} onChange={e => setForm(f => ({ ...f, common_name: e.target.value }))} />
            </div>
            <div>
              <label style={label}>Species</label>
              <input style={input} placeholder="e.g. Mangifera indica" value={form.species} onChange={e => setForm(f => ({ ...f, species: e.target.value }))} />
            </div>
            <div>
              <label style={label}>Supplier</label>
              <input style={input} value={form.supplier} onChange={e => setForm(f => ({ ...f, supplier: e.target.value }))} />
            </div>
            <div>
              <label style={label}>Lot Number</label>
              <input style={input} value={form.lot_number} onChange={e => setForm(f => ({ ...f, lot_number: e.target.value }))} />
            </div>
            <div>
              <label style={label}>Quantity (grams)</label>
              <input type="number" style={input} value={form.quantity_g} onChange={e => setForm(f => ({ ...f, quantity_g: e.target.value }))} />
            </div>
            <div>
              <label style={label}>Germination Rate (%)</label>
              <input type="number" min="0" max="100" style={input} value={form.germination_rate_pct} onChange={e => setForm(f => ({ ...f, germination_rate_pct: e.target.value }))} />
            </div>
            <div>
              <label style={label}>Storage Location</label>
              <input style={input} placeholder="e.g. Seed cabinet, Freezer A" value={form.storage_location} onChange={e => setForm(f => ({ ...f, storage_location: e.target.value }))} />
            </div>
            <div>
              <label style={label}>Purchase Date</label>
              <input type="date" style={input} value={form.purchase_date} onChange={e => setForm(f => ({ ...f, purchase_date: e.target.value }))} />
            </div>
            <div>
              <label style={label}>Expiry / Use-by Date</label>
              <input type="date" style={input} value={form.expiry_date} onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))} />
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={label}>Notes</label>
            <textarea style={{ ...input, minHeight: 56, resize: 'vertical' }} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ ...btn('primary'), padding: '8px 14px', fontSize: 13 }}
              onClick={() => createSeed.mutate({
                common_name: form.common_name, species: form.species, supplier: form.supplier, lot_number: form.lot_number,
                storage_location: form.storage_location, notes: form.notes,
                quantity_g: form.quantity_g ? parseFloat(form.quantity_g) : null,
                germination_rate_pct: form.germination_rate_pct ? parseInt(form.germination_rate_pct) : null,
                purchase_date: isoOrNull(form.purchase_date), expiry_date: isoOrNull(form.expiry_date),
              })}
              disabled={!form.common_name}>
              {createSeed.isPending ? 'Saving…' : 'Save Lot'}
            </button>
            <button style={{ ...btn('ghost'), padding: '8px 14px', fontSize: 13 }} onClick={() => setShowAddSeed(false)}>Cancel</button>
          </div>
        </div>
      )}

      {isLoading && <p style={{ color: C.muted, textAlign: 'center' }}>Loading seed inventory…</p>}

      {!isLoading && seeds.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: C.muted }}>
          <Sprout size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
          <p>No seeds in inventory yet. Add your first seed lot.</p>
        </div>
      )}

      {/* Seed table */}
      {seeds.length > 0 && (
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {['Variety', 'Species', 'Stock (g)', 'Germ. %', 'Supplier', 'Expires', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {seeds.map(seed => (
                <>
                  <tr key={seed.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: '12px 14px', color: C.text, fontWeight: 500, fontSize: 13 }}>{seed.common_name}</td>
                    <td style={{ padding: '12px 14px', color: C.muted, fontSize: 12, fontStyle: 'italic' }}>{seed.species || '—'}</td>
                    <td style={{ padding: '12px 14px', color: (seed.quantity_g ?? 0) <= 50 ? C.danger : C.text, fontSize: 13, fontWeight: 600 }}>
                      {(seed.quantity_g ?? 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '12px 14px', color: C.muted, fontSize: 13 }}>
                      {seed.germination_rate_pct != null ? `${seed.germination_rate_pct}%` : '—'}
                    </td>
                    <td style={{ padding: '12px 14px', color: C.muted, fontSize: 12 }}>{seed.supplier || '—'}</td>
                    <td style={{ padding: '12px 14px', fontSize: 12, color: seed.expiry_date && new Date(seed.expiry_date) < new Date() ? C.danger : C.muted }}>
                      {seed.expiry_date ? new Date(seed.expiry_date).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button style={btn('ghost')} onClick={() => setShowUseForm(showUseForm === seed.id ? null : seed.id)}>
                          <Minus size={12} style={{ marginRight: 4 }} />Use
                        </button>
                      </div>
                    </td>
                  </tr>
                  {showUseForm === seed.id && (
                    <tr key={`${seed.id}-use`}>
                      <td colSpan={7} style={{ padding: '12px 14px', background: 'rgba(255,255,255,0.02)' }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                          <div>
                            <label style={label}>Quantity used (g)</label>
                            <input type="number" style={{ ...input, width: 120 }} value={useForm.quantity_g} onChange={e => setUseForm(f => ({ ...f, quantity_g: e.target.value }))} />
                          </div>
                          <div>
                            <label style={label}>Purpose</label>
                            <select style={{ ...input, width: 160 }} value={useForm.purpose} onChange={e => setUseForm(f => ({ ...f, purpose: e.target.value }))}>
                              <option value="propagation">Propagation</option>
                              <option value="planting">Direct planting</option>
                              <option value="gifted">Gifted</option>
                              <option value="lost">Lost / Discarded</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                          <div style={{ flex: 1 }}>
                            <label style={label}>Notes</label>
                            <input style={input} value={useForm.notes} onChange={e => setUseForm(f => ({ ...f, notes: e.target.value }))} />
                          </div>
                          <button style={{ ...btn('danger'), padding: '7px 12px' }}
                            onClick={() => logUsage.mutate({ seedId: seed.id, body: { quantity_g: parseFloat(useForm.quantity_g) || 0, purpose: useForm.purpose, notes: useForm.notes } })}
                            disabled={!useForm.quantity_g}>
                            Log Use
                          </button>
                          <button style={btn('ghost')} onClick={() => { setShowUseForm(null); setUseForm({ quantity_g: '', purpose: 'propagation', notes: '' }) }}>Cancel</button>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  )
}
